<template>
    <div v-cloak>
        <v-card id="courses" elevation="4" class="mb-8">
            <v-toolbar color="purple" dark flat dense>
                <v-toolbar-title>{{ enrolment.CourseName }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-scroll-to="'#top'" dark text>
                    <v-icon left>fas fa-chevron-up</v-icon>
                    Top
                </v-btn>
                <span v-if="!IsAttending || IsEnrolmentRegistered"
                        class="fa-stack fa-md icon-info">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-info fa-stack-1x"></i>
                </span>
                <span v-else-if="IsAttending && IsRegistrationComplete"
                        class="fa-stack fa-md icon-success">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-check fa-stack-1x"></i>
                </span>
                <span v-else-if="IsAttending"
                        class="fa-stack fa-md icon-error">
                    <i class="fa fa-circle fa-stack-2x"></i>
                    <i class="fa fa-exclamation fa-stack-1x"></i>
                </span>
            </v-toolbar>
            <v-card-text>
                <p class="text-h6">Register for your course</p>
                <v-alert v-if="IsAttending && IsEnrolmentRegistered" type="info" outlined dense text>You are registered on this course</v-alert>
                <v-alert v-if="IsAttending && !IsEnrolmentRegistered && !IsRegistrationStarted" type="error" outlined dense text>You have not started registration for this course yet</v-alert>
                <v-alert v-if="IsAttending && !IsEnrolmentRegistered && IsRegistrationInProgress" type="error" outlined dense text>You have not completed registration for this course yet</v-alert>
                <v-alert v-if="IsAttending && !IsEnrolmentRegistered && IsRegistrationComplete" type="success" outlined dense text>Your registration is complete on this course</v-alert>

                <v-row>
                    <v-col>
                        <table border="0"
                               cellpadding="0"
                               cellspacing="5"
                               class="my-4">
                            <tr>
                                <th width="150" align="left">Course Code:</th>
                                <th align="left">{{ enrolment.CourseCode }}</th>
                            </tr>
                            <tr v-if="timetableEvent">
                                <td>Date:</td>
                                <td>{{ timetableEvent.StartDateTime | formatFullDate }} at {{ timetableEvent.StartDateTime | formatTime }}</td>
                            </tr>
                            <tr>
                                <td>Campus:</td>
                                <td>{{ timetableEvent.Campus }}</td>
                            </tr>
                            <tr>
                                <td>Location:</td>
                                <td>{{ timetableEvent.RoomNames }}</td>
                            </tr>
                        </table>
                    </v-col>
                    <v-col v-if="!!enrolmentRegistration.lastUpdated">
                        <table border="0"
                               cellpadding="0"
                               cellspacing="5"
                               class="my-4">
                            <tr v-if="!enrolmentRegistration.hasCompleted">
                                <td width="240">Last Registration Section Viewed:</td>
                                <td>{{ enrolmentRegistration.lastSectionViewed }}</td>
                            </tr>
                            <tr>
                                <td>Registration Last Updated:</td>
                                <td>{{ enrolmentRegistration.lastUpdated | formatDateTime }}</td>
                            </tr>
                        </table>
                    </v-col>
                </v-row>

                <p class="mb-0">Connect2 D&amp;A event in June - This will be a valuable part of your course preparation and we encourage you to make every effort to attend.</p>

                <v-progress-circular v-if="loading" color="info" indeterminate></v-progress-circular>
                <div v-else-if="inductionAttendance != null">
                    <v-radio-group v-model="inductionAttendance.attending"
                                   :disabled="submitted"
                                   @change="OnInductionAttendanceChange"
                                   column>
                        <v-radio label="Will attend event"
                                 color="success"
                                 :value="true"></v-radio>
                        <v-radio label="Can't attend event"
                                 color="error"
                                 :value="false"></v-radio>
                    </v-radio-group>
                </div>
            </v-card-text>
            <v-card-actions v-if="IsAttending" class="pl-4">
                <v-row>
                    <v-col v-if="!IsEnrolmentRegistered && !IsRegistrationStarted" sm="auto" class="px-1">
                        <v-btn @click="OpenRegistration" color="error" dark small style="color:white !important">
                            <v-icon left small>fas fa-pencil-alt</v-icon>
                            Click to Register
                        </v-btn>
                    </v-col>
                    <v-col v-if="IsRegistrationInProgress" sm="auto" class="px-1">
                        <v-btn @click="OpenRegistration" color="error" dark small style="color:white !important">
                            <v-icon left small>fas fa-tasks</v-icon>
                            Continue Registration
                        </v-btn>
                    </v-col>
                    <v-col v-if="IsRegistrationComplete" sm="auto" class="px-1">
                        <v-btn @click="OpenRegistration" color="success" dark small style="color:white !important">
                            <v-icon left small>fas fa-check</v-icon>
                            Registration Complete
                        </v-btn>
                    </v-col>
                    <v-col sm="auto" class="px-1">
                        <v-btn @click="OpenVideoGuidance" color="info" dark small style="color:white !important">
                            <v-icon left small>fas fa-info-circle</v-icon>
                            Registration Guidance (Video)
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
    import { eventBus } from '@/main'

    import filtersMixin from '@/views/components/mixins/base/filters.js'
    import appApiMixin from '@/views/components/mixins/appApi.js'
    import studentsMixin from '@/views/components/mixins/enterpriseDataApi/students.js'
    import timetableEventsMixin from '@/views/components/mixins/enterpriseDataApi/timetableEvents.js'
    import enrolmentRegistrationsMixin from '@/views/components/mixins/enrolmentRegistrations.js'
    import inductionAttendanceMixin from '@/views/components/mixins/inductionAttendance.js'

    export default {
        props: ['enrolment', 'enrolmentRegistration'],
        mixins: [
            filtersMixin,
            appApiMixin,
            studentsMixin,
            timetableEventsMixin,
            enrolmentRegistrationsMixin,
            inductionAttendanceMixin,
        ],
        data() {
            return {
                loading: false,
                submitted: false,
            }
        },
        computed: {
            IsEnrolmentRegistered() { return ['L', 'R'].indexOf(this.enrolment.StatusCode) > -1; },
            IsAttending() { return this.inductionAttendance?.attending || false }
        },
        mounted() {
            eventBus.$off('student-fetch:success').$on('student-fetch:success', () => this.LoadInductionAttendance(this.student.StudentID, this.enrolment.CourseCode));
            eventBus.$off('induction-attendance-fetch:started').$on('induction-attendance-fetch:started', () => this.loading = true);
            eventBus.$off('induction-attendance-fetch:finished').$on('induction-attendance-fetch:finished', () => this.loading = false);
            eventBus.$off('induction-attendance-save:started').$on('induction-attendance-save:started', () => this.submitted = true);
            eventBus.$on('induction-attendance-save:success', response => {
                eventBus.$emit('alert:success', response);
                this.LoadInductionAttendance(this.student.StudentID, this.enrolment.CourseCode);
            });
            eventBus.$off('induction-attendance-save:finished').$on('induction-attendance-save:finished', () => this.submitted = false);

            //this.LoadStudent();
            this.LoadFirstTimetableEvent(this.enrolment.CourseCode);
        },
        methods: {
            OpenRegistration() {
                let path = `/enrolment/${this.enrolment.EnrolmentID}/${this.enrolment.AcademicSession}`;
                if (this.enrolment.OnboardingTypeCode == 'Short') path += '/short';

                let lastSectionViewed = this.TranslatePageToPath(this.enrolmentRegistration?.lastSectionViewed);

                if (lastSectionViewed != null) path += lastSectionViewed;

                this.$router.push({ path: path });
            },
            OnInductionAttendanceChange() { this.SaveInductionAttendance(this.student.StudentReference, this.StudentDisplayName); },
            OpenVideoGuidance() {
                window.open('https://web.microsoftstream.com/video/9c3ee033-aafe-42ea-9b4e-d93de250457d', '_blank');
            }
        },
    }
</script>